import { mailApiGet, mailApiPost } from "./client";
import type {
  MailDeletePayload,
  MailDeleteResponse,
  MailMovePayload,
  MailMoveResponse,
  MailSaveDraftPayload,
  MailSaveDraftResponse,
  MailSendPayload,
  MailSendResponse,
} from "@/lib/types/compose";

const MAIL_SEND_TIMEOUT_MS = 90_000;
const MAIL_DRAFT_TIMEOUT_MS = 120_000;

export function sendMailMessage(payload: MailSendPayload) {
  return mailApiPost<MailSendResponse>("send-message.php", payload, {
    timeoutMs: MAIL_SEND_TIMEOUT_MS,
  });
}

export type ImmediateSendFailure = {
  clientKey: string;
  accountId: number;
  subject: string;
  error?: string;
  to: string;
  cc?: string;
  bcc?: string;
  bodyText: string;
  bodyHtml?: string;
  attachments?: MailSendPayload["attachments"];
  inReplyTo?: string;
  references?: string;
  sentFolder?: string;
};

export function fetchImmediateSendFailures() {
  return mailApiGet<{ success: boolean; items: ImmediateSendFailure[] }>(
    "send-message.php?outbox=1"
  );
}

export function saveMailDraft(payload: MailSaveDraftPayload) {
  return mailApiPost<MailSaveDraftResponse>("imap/save-draft.php", payload, {
    timeoutMs: MAIL_DRAFT_TIMEOUT_MS,
  });
}

export function deleteMailMessage(
  payload: MailDeletePayload,
  options?: { timeoutMs?: number }
) {
  const uids =
    payload.uids && payload.uids.length > 0
      ? payload.uids.filter((uid) => uid > 0)
      : payload.uid != null && payload.uid > 0
        ? [payload.uid]
        : [];

  if (uids.length === 0) {
    return Promise.resolve({
      ok: false as const,
      status: 400,
      message: "Message invalide.",
    });
  }

  if (uids.length === 1) {
    return mailApiPost<MailDeleteResponse>(
      "imap/delete.php",
      {
        accountId: payload.accountId,
        folder: payload.folder,
        uid: uids[0],
        trashFolder: payload.trashFolder ?? undefined,
      },
      { timeoutMs: options?.timeoutMs ?? MAIL_DRAFT_TIMEOUT_MS }
    );
  }

  return mailApiPost<MailDeleteResponse>(
    "imap/delete.php",
    {
      accountId: payload.accountId,
      folder: payload.folder,
      uids,
      trashFolder: payload.trashFolder ?? undefined,
    },
    { timeoutMs: options?.timeoutMs ?? MAIL_DRAFT_TIMEOUT_MS }
  );
}

export function moveMailMessage(
  payload: MailMovePayload,
  options?: { timeoutMs?: number }
) {
  const uids =
    payload.uids && payload.uids.length > 0
      ? payload.uids.filter((uid) => uid > 0)
      : payload.uid != null && payload.uid > 0
        ? [payload.uid]
        : [];

  if (uids.length === 0) {
    return Promise.resolve({
      ok: false as const,
      status: 400,
      message: "Message invalide.",
    });
  }

  if (uids.length === 1) {
    return mailApiPost<MailMoveResponse>(
      "imap/move.php",
      {
        accountId: payload.accountId,
        folder: payload.folder,
        uid: uids[0],
        targetFolder: payload.targetFolder,
      },
      options
    );
  }

  return mailApiPost<MailMoveResponse>(
    "imap/move.php",
    {
      accountId: payload.accountId,
      folder: payload.folder,
      uids,
      targetFolder: payload.targetFolder,
    },
    options
  );
}
